import React, { useState } from "react";
import { useForm, Link as InertiaLink } from "@inertiajs/react";
import {
  Card,
  CardBody,
  Input,
  Textarea,
  Select,
  SelectItem,
  Button,
  Link,
  RadioGroup,
  Radio,
  Tooltip,
  Chip,
} from "@heroui/react";
import Layout from "../Layouts/Layout";

export default function New({
  visibility_options,
  model_options,
  task = null,
  errors = [],
}) {
  const isEditing = !!task;

  const { data, setData, post, put, processing } = useForm({
    name: task?.name || "",
    description: task?.description || "",
    visibility: task?.visibility || "private",
    selected_model_id: task?.selected_model_id || "",
    instructions: task?.instructions || "",
    rules: task?.rules || "",
    examples: task?.examples || [],
    output_format: task?.output_format || "plain_text",
    output_schema: task?.output_schema || "",
  });

  const [examplesList, setExamplesList] = useState(task?.examples || [""]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Parse examples
    const parsedExamples = examplesList.filter((ex) => ex.trim());

    const submitData = {
      task: {
        ...data,
        examples: parsedExamples,
      },
    };

    if (isEditing) {
      put(`/tasks/${task.id}`, submitData);
    } else {
      post("/tasks", submitData);
    }
  };

  const addExample = () => {
    setExamplesList([...examplesList, ""]);
  };

  const removeExample = (index) => {
    const newList = examplesList.filter((_, idx) => idx !== index);
    setExamplesList(newList);
  };

  const updateExample = (index, value) => {
    const newList = [...examplesList];
    newList[index] = value;
    setExamplesList(newList);
  };

  const getOutputFormatIcon = (format) => {
    switch (format) {
      case "plain_text":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h7"
            />
          </svg>
        );
      case "markdown":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
            />
          </svg>
        );
      case "json":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
            />
          </svg>
        );
      case "xml":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
            />
          </svg>
        );
      case "csv":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            as={InertiaLink}
            href="/tasks"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors mb-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Tasks
          </Link>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
              {isEditing ? (
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {isEditing ? "Edit task" : "Create new task"}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {isEditing
                  ? "Modify your AI task configuration"
                  : "Configure a new AI-powered task"}
              </p>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <Card className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 mb-6">
            <CardBody>
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-200 mb-2">
                    Please fix the following errors:
                  </p>
                  <ul className="list-disc list-inside text-red-700 dark:text-red-300 space-y-1">
                    {errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600 dark:text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Basic information
                </h2>
              </div>

              <div className="space-y-4">
                <Input
                  label="Task name"
                  placeholder="e.g., Summarize Article, Generate Email Response"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  isRequired
                  variant="bordered"
                  startContent={
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  }
                  classNames={{
                    inputWrapper:
                      "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                  }}
                />

                <Textarea
                  label="Description"
                  placeholder="Describe what this task does and when to use it..."
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  minRows={3}
                  variant="bordered"
                  classNames={{
                    inputWrapper:
                      "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Visibility"
                    selectedKeys={[data.visibility]}
                    onChange={(e) => setData("visibility", e.target.value)}
                    variant="bordered"
                    startContent={
                      data.visibility === "private" ? (
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      )
                    }
                    classNames={{
                      trigger:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                  >
                    {visibility_options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </Select>

                  <Select
                    label="AI model"
                    selectedKeys={
                      data.selected_model_id
                        ? [String(data.selected_model_id)]
                        : []
                    }
                    onChange={(e) =>
                      setData(
                        "selected_model_id",
                        e.target.value ? parseInt(e.target.value) : ""
                      )
                    }
                    variant="bordered"
                    placeholder="Select a model"
                    isRequired
                    startContent={
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    }
                    description={
                      model_options.length === 0
                        ? "No API keys configured. Add one in API Keys settings."
                        : undefined
                    }
                    classNames={{
                      trigger:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                  >
                    {model_options.map((option) => (
                      <SelectItem
                        key={String(option.value)}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Prompt Configuration */}
          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-purple-600 dark:text-purple-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Prompt configuration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Define how the AI should process your task
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Textarea
                    label="Instructions"
                    placeholder="Describe what the AI should do. Be specific about the task, expected behavior, and any constraints..."
                    value={data.instructions}
                    onChange={(e) => setData("instructions", e.target.value)}
                    minRows={6}
                    variant="bordered"
                    classNames={{
                      input: "font-mono text-sm",
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Tip: Include context about input format, desired tone, and
                    specific requirements
                  </p>
                </div>

                <div>
                  <Textarea
                    label="Rules (Optional)"
                    placeholder="Add specific rules or constraints, e.g.:
- Keep responses under 500 words
- Always cite sources
- Avoid technical jargon
- Format dates as YYYY-MM-DD"
                    value={data.rules}
                    onChange={(e) => setData("rules", e.target.value)}
                    minRows={4}
                    variant="bordered"
                    classNames={{
                      input: "font-mono text-sm",
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                  />
                </div>

                {/* Examples Section */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                        Examples (optional)
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Provide input/output examples to guide the AI
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="flat"
                      className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                      onClick={addExample}
                      startContent={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      }
                    >
                      Add example
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {examplesList.map((example, idx) => (
                      <div
                        key={idx}
                        className="relative bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Chip
                            size="sm"
                            variant="flat"
                            className="bg-gray-200 dark:bg-gray-700"
                          >
                            Example {idx + 1}
                          </Chip>
                          {examplesList.length > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              color="danger"
                              variant="light"
                              onClick={() => removeExample(idx)}
                              isIconOnly
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </Button>
                          )}
                        </div>
                        <Textarea
                          value={example}
                          onChange={(e) => updateExample(idx, e.target.value)}
                          minRows={3}
                          variant="bordered"
                          placeholder={`Input: [your input here]
Output: [expected output here]`}
                          classNames={{
                            input: "font-mono text-sm",
                            inputWrapper:
                              "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Output Configuration */}
          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Output configuration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Define the format and structure of AI responses
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <RadioGroup
                  label="Output format"
                  orientation="horizontal"
                  value={data.output_format}
                  onValueChange={(value) => setData("output_format", value)}
                  classNames={{
                    wrapper: "gap-4 ml-2",
                  }}
                >
                  {[
                    { value: "plain_text", label: "Plain text" },
                    { value: "markdown", label: "Markdown" },
                    { value: "json", label: "JSON" },
                    { value: "xml", label: "XML" },
                    { value: "csv", label: "CSV" },
                  ].map((format) => (
                    <Tooltip key={format.value} content={`Output as ${format.label}`}>
                      <Radio
                        value={format.value}
                        classNames={{
                          base: "border-2 mr-2 border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-amber-500 dark:hover:border-amber-500 data-[selected=true]:border-amber-500 transition-colors",
                          label: "flex items-center gap-2",
                        }}
                      >
                        <span className="flex items-center gap-2">
                          {getOutputFormatIcon(format.value)}
                          {format.label}
                        </span>
                      </Radio>
                    </Tooltip>
                  ))}
                </RadioGroup>

                <Textarea
                  label="Output schema (optional)"
                  placeholder={`Define the structure of your output. For JSON, you can use JSON Schema:
{
  "type": "object",
  "properties": {
    "summary": { "type": "string" },
    "key_points": { "type": "array", "items": { "type": "string" } },
    "sentiment": { "type": "string", "enum": ["positive", "negative", "neutral"] }
  }
}`}
                  value={data.output_schema}
                  onChange={(e) => setData("output_schema", e.target.value)}
                  minRows={8}
                  variant="bordered"
                  classNames={{
                    input: "font-mono text-sm",
                    inputWrapper:
                      "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                  }}
                />
              </div>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              size="lg"
              isLoading={processing}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-shadow"
              startContent={
                !processing && (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )
              }
            >
              {processing
                ? "Saving..."
                : isEditing
                  ? "Update task"
                  : "Create task"}
            </Button>
            <Button
              as={InertiaLink}
              href="/tasks"
              variant="bordered"
              size="lg"
              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
